import React from "react";
import { Clock, Moon, Sunrise, Zap } from "lucide-react";

const MEAL_ORDER = ["breakfast", "lunch", "dinner", "snack"];

const fmtMins = (m) => {
  if (m == null) return "—";
  const t = Math.round(m);
  return `${Math.floor(t / 60)}h ${String(t % 60).padStart(2, "0")}m`;
};

// 24-hour kcal distribution + first/last meal, per-meal averages, late-night
// energy and post-session refuel gap.
export default function EatingTimesCard({ eating, rangeLabel }) {
  const peak = Math.max(...eating.hourly.map((h) => h.kcal), 1);
  const late = eating.lateNight;
  const refuel = eating.refuel;

  return (
    <div className="space-y-4" data-testid="insights-eating-times">
      <section className="border border-white/10 bg-[#141414] p-5">
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <p className="text-[10px] uppercase tracking-[0.28em] text-zinc-400">When your energy lands · last {rangeLabel}</p>
          <p className="text-[10px] font-mono text-zinc-500">{eating.coverage.timed} of {eating.coverage.total} entries timed</p>
        </div>

        <div className="mt-5 flex items-end gap-[2px] h-32" data-testid="eating-hourly-chart">
          {eating.hourly.map((h) => {
            const pct = Math.round((h.kcal / peak) * 100);
            const isLate = h.hour >= 21 || h.hour < 5;
            return (
              <div key={h.hour} className="flex-1 flex flex-col justify-end h-full group relative"
                title={`${String(h.hour).padStart(2, "0")}:00 — ${h.kcal} kcal · ${h.items} item${h.items === 1 ? "" : "s"}`}>
                <div className="w-full transition-all duration-500 hover:opacity-100"
                  style={{ height: `${Math.max(pct, h.kcal ? 3 : 0)}%`,
                           background: isLate ? "#FF9500" : "#4C9AFF",
                           opacity: h.kcal ? 0.9 : 0.12,
                           minHeight: h.kcal ? 3 : 2 }} />
              </div>
            );
          })}
        </div>
        <div className="flex justify-between text-[9px] font-mono text-zinc-500 mt-1.5">
          {["00", "06", "12", "18", "23"].map((l) => <span key={l}>{l}:00</span>)}
        </div>
        <p className="text-[11px] text-zinc-400 mt-3">Blue = daytime fuelling · amber = late-night / pre-5am energy.</p>
      </section>

      <div className="grid sm:grid-cols-3 gap-4">
        <section className="border border-white/10 bg-[#141414] p-5" data-testid="eating-window-card">
          <div className="flex items-center gap-2 mb-3">
            <Sunrise className="h-4 w-4 text-[#E2E30A]" />
            <p className="text-[10px] uppercase tracking-[0.28em] text-zinc-400">Eating window</p>
          </div>
          <p className="font-display text-4xl leading-none text-white">{fmtMins(eating.eatingWindowMins)}</p>
          <p className="text-[11px] text-zinc-400 mt-2">
            First meal <b className="text-white font-mono">{eating.firstMealAvg || "—"}</b> · last <b className="text-white font-mono">{eating.lastMealAvg || "—"}</b>
          </p>
          <p className="text-[11px] text-zinc-500 mt-1">Overnight gap ≈ {fmtMins(eating.overnightFastMins)}</p>
        </section>

        <section className="border border-white/10 bg-[#141414] p-5" data-testid="eating-per-meal-card">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="h-4 w-4 text-[#4C9AFF]" />
            <p className="text-[10px] uppercase tracking-[0.28em] text-zinc-400">Average meal times</p>
          </div>
          <ul className="space-y-1.5">
            {MEAL_ORDER.filter((m) => eating.perMeal[m]).map((m) => (
              <li key={m} className="flex items-baseline justify-between text-sm">
                <span className="capitalize text-zinc-300">{m}</span>
                <span className="font-mono text-white">{eating.perMeal[m]}
                  <span className="text-[10px] text-zinc-500 ml-1.5">×{eating.perMealCounts[m]}</span>
                </span>
              </li>
            ))}
            {!MEAL_ORDER.some((m) => eating.perMeal[m]) && (
              <li className="text-sm text-zinc-400">No timed meals yet.</li>
            )}
          </ul>
        </section>

        <section className="border border-white/10 bg-[#141414] p-5" data-testid="eating-flags-card">
          <div className="flex items-center gap-2 mb-3">
            <Zap className="h-4 w-4 text-[#34C759]" />
            <p className="text-[10px] uppercase tracking-[0.28em] text-zinc-400">Refuel &amp; late nights</p>
          </div>
          <p className="text-sm text-zinc-300">Post-session refuel</p>
          <p className="font-display text-3xl leading-none mt-1"
            style={{ color: refuel.avgGapMins == null ? "#3f3f46" : refuel.avgGapMins <= 60 ? "#34C759" : refuel.avgGapMins <= 120 ? "#E2E30A" : "#FF9500" }}>
            {refuel.avgGapMins == null ? "—" : fmtMins(refuel.avgGapMins)}
          </p>
          <p className="text-[11px] text-zinc-500 mt-1">
            {refuel.samples ? `Average gap after training across ${refuel.samples} day${refuel.samples === 1 ? "" : "s"} — aim under 60m.` : "Log food on a training day to see this."}
          </p>
          <div className="mt-4 pt-4 border-t border-white/10">
            <div className="flex items-center gap-1.5">
              <Moon className="h-3.5 w-3.5 text-[#FF9500]" />
              <p className="text-sm text-zinc-300">After 21:00</p>
            </div>
            <p className="font-display text-2xl leading-none mt-1" style={{ color: late.pct > 25 ? "#FF9500" : "#34C759" }}>
              {late.kcal} kcal <span className="text-sm text-zinc-400">· {late.pct}% of intake</span>
            </p>
            <p className="text-[11px] text-zinc-500 mt-1">Across {late.days} day{late.days === 1 ? "" : "s"}.</p>
          </div>
        </section>
      </div>
    </div>
  );
}
